/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition, DataAxisProps, Dimension } from '../axis.types';
import { DataAxisInfo } from '../dataAxisInfo';
import { AXIS_DEFAULTS } from './axisDefaults';
import { getAxisMaxSize, getAxisRange } from './axisUtils';
import { getLabelDimensions, getMaxTextDimensions, updateLabelsDims } from './labelUtils';
import { getTitleProps } from './titleUtils';
import type { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;

/**
 * The function that returns the y axis properties.
 * @param dataAxisInfo The DataAxisInfo.
 * @param dataAxis The dataAxis props.
 * @param groups The chart groups.
 * @param position The position of axis in the given space.
 * @param availSpace The available space.
 * @param isRtl Whether the reading direction is right to left.
 * @param getTextDimensions Function for text measurement.
 * @returns
 */
export function getDataAxisProps(
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  dataAxis?: DataAxisProps,
  dataAxisInfo?: DataAxisInfo,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensions
) {
  const [start, end] = getAxisRange(availSpace, position, isRtl);
  dataAxisInfo = dataAxisInfo || new DataAxisInfo({ ...dataAxis });
  const scale = dataAxisInfo.getScale([start, end]);

  const isHoriz = position === 'top' || position === 'bottom';
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, dataAxis?.size);
  const titleStyle = { fontSize: axisFontSize, ...dataAxis?.titleStyle };
  const {
    width: titleWidth,
    height: titleHeight,
    ...titleProps
  } = getTitleProps(
    dataAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    getTextDimensions
  );

  const gap = AXIS_DEFAULTS.labelGap;
  const isTitleRendered = titleWidth > 0 && titleWidth > 0;
  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: dataAxis?.tickLabel?.style?.color,
    ...dataAxis?.tickLabel?.style,
    textAnchor: position === 'left' ? 'end' : position === 'bottom' ? 'middle' : 'start',
    dominantBaseline: 'middle'
  };
  const ticks = dataAxisInfo.getTicks();
  const labels = ticks.map((i: number) => i.toString());
  const getLabelDims = (tick: string, index: number) => {
    const coord = scale.transform(ticks[index]);
    return getLabelDimensions(coord, tick, tickLabelStyle, isHoriz, availSpace, getTextDimensions);
  };

  const { maxWidth, maxHeight, tickLabels } = getMaxTextDimensions(labels, getLabelDims, isHoriz);

  const isLabelRendered = isHoriz
    ? maxHeight + gap < axisMaxDim - titleHeight
    : maxWidth + gap < axisMaxDim - titleHeight;

  const getAxisPos = (index: number) => {
    return scale.transform(ticks[index]);
  };
  if (isLabelRendered) {
    updateLabelsDims(maxWidth, maxHeight, tickLabels, getAxisPos, availSpace, position, isRtl);
  }

  let axisDim = isLabelRendered ? (isHoriz ? maxHeight + gap : maxWidth + gap) : 0;
  if (isTitleRendered) {
    axisDim += titleHeight;
  }
  return {
    title: isTitleRendered ? dataAxis?.title : undefined,
    titleProps: isTitleRendered ? titleProps : undefined,
    axisDim,
    tickLabels,
    tickLabelStyle,
    scale
  };
}
